// src/hooks/useMedicinePrices.ts
/**
 * 指定した service_type の薬品価格一覧をバックエンドから取得するカスタムフック。
 * DBにデータがない場合は webPriceMapping.ts の静的データにフォールバック。
 */
import { useEffect, useState } from 'react';
import { webPrices } from '../utils/webPriceMapping';
import { translateMedicineName } from '../utils/medTranslations';

export interface PricePlanItem {
  id: number;
  plan_type: 'single_purchase' | 'subscription';
  duration_months: number;
  billing_unit: 'month' | 'quantity';
  quantity_units: number | null;
  coverage_months_min: number | null;
  coverage_months_max: number | null;
  price_tax_excluded: number;
  price_tax_included: number;
}

export interface MedicineItem {
  id: number;
  name: string;
  type: 'single' | 'set';
  service_type: string;
  note: string | null;
  price_plans: PricePlanItem[];
}

/** 表示用に整形された価格文字列の行 */
export interface FormattedPriceLine {
  /** 薬品名 + 規格 (例: "マンジャロ 2.5mg　4本") */
  label: string;
  /** 税抜価格（数値） */
  priceExcluded: number;
  /** 税込価格（数値） */
  priceIncluded: number;
}

/**
 * DBデータを「表示用テキスト行」に変換する。
 * 例: "マンジャロ 2.5mg　4本（1か月）　13,000円 (税込 14,300円)"
 */
export function formatPriceLine(med: MedicineItem, lang: 'ja' | 'zh'): string[] {
  const nf = new Intl.NumberFormat('ja-JP');
  const lines: string[] = [];

  for (const plan of med.price_plans) {
    if (plan.plan_type === 'single_purchase') {
      let quantityStr = '';
      if (plan.billing_unit === 'quantity' && plan.quantity_units) {
        quantityStr = lang === 'ja'
          ? `　${plan.quantity_units}本（1か月）`
          : `　${plan.quantity_units}片（1个月）`;
      } else if (plan.billing_unit === 'month') {
        quantityStr = lang === 'ja' ? '　（1か月）' : '　（1个月）';
      }

      const priceStr = lang === 'ja'
        ? `${nf.format(plan.price_tax_excluded)}円 (税込 ${nf.format(plan.price_tax_included)}円)`
        : `${nf.format(plan.price_tax_excluded)} 日元 (含税 ${nf.format(plan.price_tax_included)} 日元)`;

      const translatedName = translateMedicineName(med.name, lang);
      lines.push(`${translatedName}${quantityStr}　${priceStr}`);
    }
  }

  return lines;
}

/** 既存メニューページの priceItems と同形の表示行 */
export interface DisplayPriceItem {
  name: string;      // 翻訳済みの薬品名（規格mg含む。例: "マンジャロ 2.5mg (Mounjaro)"）
  dosage: string;    // 数量表記（例: "4本(1か月)"）
  price: number;     // 税抜
  taxPrice: number;  // 税込
}

/**
 * DB(API)の medicines を、既存メニューページの priceItems 形状に変換する（★単次価 single_purchase のみ）。
 * subscription(定期便) は「機能未上線」のため表示に含めない。空配列ならページ側でハードコードにフォールバックする。
 * ※ medicines は useMedicinePrices が返す「翻訳済み」の配列を渡すこと。
 */
export function toSinglePriceItems(medicines: MedicineItem[], lang: 'ja' | 'zh'): DisplayPriceItem[] {
  const items: DisplayPriceItem[] = [];
  for (const med of medicines) {
    const single = med.price_plans.find(p => p.plan_type === 'single_purchase');
    if (!single) continue;
    const qty = single.quantity_units;
    // 単位: 注射剤(マンジャロ/Mounjaro/替尔泊肽注射 等)は「本/支」、それ以外(内服)は「錠/片」。
    // ※将来的には Medicine に単位フィールドを持たせるのが本筋（現状はDBに単位が無いため名称から推定）。
    const isInjectable = /マンジャロ|Mounjaro|注射/.test(med.name);
    const unitJa = isInjectable ? '本' : '錠';
    const unitZh = isInjectable ? '支' : '片';
    // 規格表記の優先度: ①数量(quantity_units)があれば言語別に生成(i18n対応) → ②無ければ補足(note)を流用
    //  → ③どちらも無ければ「(1か月)」。※本番は補足に規格を入れる運用なので、note フォールバックで表示が壊れない。
    const dosage = qty
      ? (lang === 'ja' ? `${qty}${unitJa}(1か月)` : `${qty}${unitZh}(1个月)`)
      : (med.note && med.note.trim() ? med.note.trim() : (lang === 'ja' ? '(1か月)' : '(1个月)'));
    items.push({
      name: med.name,
      dosage,
      price: single.price_tax_excluded,
      taxPrice: single.price_tax_included,
    });
  }
  return items;
}

/** LP 料金表ポップアップ用のグループ(薬品→規格variant)。既存 priceGroups と同形。 */
export interface DisplayPriceGroup {
  name: string;
  variants: { dosage: string; single: string; sub: string | null }[];
}

/**
 * LP の「参考料金」ポップアップ用: medicines を priceGroups 形状に変換する（★単次価のみ）。
 * 薬品名から規格(mg)を抽出して「薬品グループ→規格variant」に束ねる。single は税抜文字列
 * （ポップアップ側が ×1.1 で税込を計算するため）。空配列ならページ側で locale の priceGroups にフォールバック。
 */
export function toPriceGroups(medicines: MedicineItem[], lang: 'ja' | 'zh'): DisplayPriceGroup[] {
  const nf = new Intl.NumberFormat('ja-JP');
  const groups: DisplayPriceGroup[] = [];
  const byDrug = new Map<string, DisplayPriceGroup>();
  for (const med of medicines) {
    const single = med.price_plans.find(p => p.plan_type === 'single_purchase');
    if (!single) continue;
    // 名称から規格mgを抽出（例: "マンジャロ 2.5mg (Mounjaro)" → mg="2.5mg", drug="マンジャロ (Mounjaro)"）
    const mgMatch = med.name.match(/(\d+(?:\.\d+)?)\s*mg/i);
    let mg = '', drug = med.name;
    if (mgMatch) {
      mg = mgMatch[0].replace(/\s+/g, '');
      drug = med.name.replace(mgMatch[0], '').replace(/\s{2,}/g, ' ').trim();
    }
    const qty = single.quantity_units;
    const isInjectable = /マンジャロ|Mounjaro|注射/.test(med.name);
    const unit = lang === 'ja' ? (isInjectable ? '本' : '錠') : (isInjectable ? '支' : '片');
    const qtyStr = qty
      ? (lang === 'ja' ? `${qty}${unit}（1か月）` : `${qty}${unit}（1个月）`)
      : (lang === 'ja' ? '（1か月）' : '（1个月）');
    const dosage = mg ? `${mg} / ${qtyStr}` : (med.note && med.note.trim() ? med.note.trim() : qtyStr);
    const singleStr = lang === 'ja'
      ? `${nf.format(single.price_tax_excluded)}円`
      : `${nf.format(single.price_tax_excluded)} 日元`;
    let g = byDrug.get(drug);
    if (!g) { g = { name: drug, variants: [] }; byDrug.set(drug, g); groups.push(g); }
    g.variants.push({ dosage, single: singleStr, sub: null });
  }
  return groups;
}

/**
 * webPriceMapping の静的データから同じ形式のテキスト行を生成する（フォールバック用）
 */
function getFallbackPriceLines(serviceType: string, lang: 'ja' | 'zh'): string[] {
  const nf = new Intl.NumberFormat('ja-JP');
  const taxRate = 1.1;

  return webPrices
    .filter(w => w.service_type === serviceType)
    .map(w => {
      const inc = Math.round(w.price_tax_excluded * taxRate);
      const quantityStr = w.billing_unit === 'quantity' && w.quantity_units
        ? (lang === 'ja' ? `　${w.quantity_units}本（${w.coverage_months}か月）` : `　${w.quantity_units}片（${w.coverage_months}个月）`)
        : (lang === 'ja' ? `　（${w.coverage_months}か月）` : `　（${w.coverage_months}个月）`);

      const priceStr = lang === 'ja'
        ? `${nf.format(w.price_tax_excluded)}円 (税込 ${nf.format(inc)}円)`
        : `${nf.format(w.price_tax_excluded)} 日元 (含税 ${nf.format(inc)} 日元)`;

      const translatedName = translateMedicineName(w.name, lang);
      return `${translatedName}${quantityStr}　${priceStr}`;
    });
}

interface UseMedicinePricesResult {
  priceLines: string[];
  medicines: MedicineItem[];
  loading: boolean;
  fromDB: boolean;
}

/**
 * 指定 service_type の価格行リストを返すフック。
 * @param serviceType  e.g. 'obesity_male', 'ed_premature', 'aga_male' ...
 * @param lang         現在の表示言語 'ja' | 'zh'
 */
export function useMedicinePrices(
  serviceType: string,
  lang: 'ja' | 'zh'
): UseMedicinePricesResult {
  const [medicines, setMedicines] = useState<MedicineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromDB, setFromDB] = useState(false);

  useEffect(() => {
    // service_type が空 or センチネル(__none__)ならフェッチしない。
    // (LP初期表示での全件取得や、DB非連動サービスの無駄なリクエストを回避)
    if (!serviceType || serviceType === '__none__') {
      setMedicines([]);
      setFromDB(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch(`/api/public/medicines?service_type=${encodeURIComponent(serviceType)}`, {
      headers: { 'Accept': 'application/json' },
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<MedicineItem[]>;
      })
      .then(data => {
        if (!cancelled) {
          setMedicines(data);
          setFromDB(data.length > 0);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMedicines([]);
          setFromDB(false);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [serviceType]);

  const localizedMedicines = medicines.map(m => ({
    ...m,
    name: translateMedicineName(m.name, lang)
  }));

  const priceLines: string[] = fromDB
    ? localizedMedicines.flatMap(med => formatPriceLine(med, lang))
    : getFallbackPriceLines(serviceType, lang);

  return { priceLines, medicines: localizedMedicines, loading, fromDB };
}
